import { Parcel } from "../services/parcelTypes";

export function formatCurrency(value: unknown): string {
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return "—";
    return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(n);
}

export function formatNumber(value: unknown): string {
    if (value === null || value === undefined || value === "") return "—";

    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return "—";
    return new Intl.NumberFormat().format(n);
}

export function formatAcre(value: unknown): string {
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return "—";
    return `${n.toFixed(2)} ac`;
}

export function formatLatLon(lat?: unknown, lon?: unknown): string {
    const la = typeof lat === "number" ? lat : Number(lat);
    const lo = typeof lon === "number" ? lon : Number(lon);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return "—";
    return `${la.toFixed(6)}, ${lo.toFixed(6)}`;
}

export function formatDateTime(iso?: unknown): string {
    if (typeof iso !== "string" || !iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).format(d);
}

export function formatDate(value: string | null | undefined): string {
    if (!value) return "—";

    // Handles "2026-04-01" OR ISO
    const datePart = value.split("T")[0];
    const [year, month, day] = datePart.split("-").map(Number);

    if (!year || !month || !day) return "—";

    const date = new Date(year, month - 1, day);

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(date);
}

export function toDateInputValue(value: string | null | undefined): string {
    if (!value) return "";
    return value.split("T")[0];
}

export function yesNoUnknown(value: boolean | null | undefined) {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "—";
}

export function formatPlannedUse(value: string | null | undefined) {
    return value && value.trim() ? value : "—";
}

export function formatParkingType(value: string | null | undefined) {
    return value && value.trim() ? value : "—";
}

export function formatOptionalNumber(value: number | null | undefined) {
    return value === null || value === undefined ? "—" : formatNumber(value);
}


// Phone validation
export function normalizePhoneDigits(value: string | null | undefined): string {
  if (!value) return "";
  return value.replace(/\D/g, "");
}

export function formatPhoneInput(value: string | null | undefined): string {
  const digits = normalizePhoneDigits(value).slice(0, 10);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function isValidPhone(value: string | null | undefined): boolean {
  if (!value || !value.trim()) return true;
  return normalizePhoneDigits(value).length === 10;
}

export function toTitleCase(input?: string | null): string {
    if (!input) return "";

    const smallWords = new Set([
        "and", "or", "the", "of", "in", "on", "at", "to", "for", "by", "a", "an"
    ]);

    return input
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .map((word, index, arr) => {
            const isFirst = index === 0;
            const isLast = index === arr.length - 1;

            // Handle hyphenated words (e.g., mixed-use)
            const parts = word.split("-").map((part, i, partsArr) => {
                const isHyphenFirst = i === 0;
                const isHyphenLast = i === partsArr.length - 1;

                if (
                    !isFirst &&
                    !isLast &&
                    !isHyphenFirst &&
                    !isHyphenLast &&
                    smallWords.has(part)
                ) {
                    return part;
                }

                // Handle apostrophes (e.g., o'connor)
                return part.replace(/(^|\b)([a-z])/g, (match) =>
                    match.toUpperCase()
                );
            });

            const rebuilt = parts.join("-");

            if (!isFirst && !isLast && smallWords.has(rebuilt)) {
                return rebuilt;
            }

            return rebuilt.replace(/(^|\b)([a-z])/g, (match) =>
                match.toUpperCase()
            );
        })
        .join(" ");
}


export const LAND_USE_CATEGORY_COLORS: Record<string, string> = {
  "Single Family": "#2E7D6B",
  "Multi-Family": "#2E7D6B",
  "Senior Housing": "#2E7D6B",
  "Commercial": "#21506F",
  "Office": "#C8743A",
  "Institutional": "#C8743A",
  "Religious Services": "#6B5FA7",
  "Industrial": "#4A6670",
  "Utilities": "#4A6670",
  "Vacant": "#6B7280",
  "Park": "#2E7D6B",
};

export function getLandUseColor(category?: string | null): string {
  if (!category) return "#6B7280"; // fallback
  return LAND_USE_CATEGORY_COLORS[category] ?? "#6B7280";
}

export function getHighlights(p: Parcel) {
    const pin = p.pin;

    const ownerRaw = p.ownership?.ownerName?.trim();
    const propertyNameRaw = p.ownership?.propertyName?.trim();

    const isInvalidValue = (value?: string | null) =>
        !value || value === "N/A" || value === "—";

    const primaryDisplay = !isInvalidValue(propertyNameRaw)
        ? { type: "property" as const, value: propertyNameRaw }
        : !isInvalidValue(ownerRaw)
            ? { type: "owner" as const, value: ownerRaw }
            : { type: "property" as const, value: "—" };

    const landUseCategory = p.landUse?.category ?? null;
    const landUse =
        p.landUse?.description ??
        p.landUse?.category ??
        "—";

    const acres = p.site?.lot?.acres;
    const lotSqft = p.site?.lot?.sqft;
    const appraisedTotal = p.valuation?.appraised?.total;
    const taxYear = p.valuation?.taxYear;

    return {
        pin,
        primaryDisplay,
        landUse,
        landUseCategory,
        acres,
        lotSqft,
        appraisedTotal,
        taxYear,
    };
}

export const getContactChipLabel = (contact: any) => {
    const staffName = contact?.staffName?.trim();
    const category = contact?.staffCategory?.trim();
    const contactDate = formatDate(contact?.contactDate);

    if (staffName) return staffName;
    if (category && contactDate !== "—") return `${category} · ${contactDate}`;
    if (category) return category;
    if (contactDate !== "—") return contactDate;
    return "Untitled contact";
};

export const NAICS_MAP = {
    "11": "Agriculture, Forestry, Fishing and Hunting",
    "21": "Mining",
    "22": "Utilities",
    "23": "Construction",
    "31-33": "Manufacturing",
    "42": "Wholesale Trade",
    "44-45": "Retail Trade",
    "48-49": "Transportation and Warehousing",
    "51": "Information",
    "52": "Finance and Insurance",
    "53": "Real Estate Rental and Leasing",
    "54": "Professional, Scientific, and Technical Services",
    "55": "Management of Companies and Enterprises",
    "56": "Administrative and Support and Waste Management and Remediation Services",
    "61": "Educational Services",
    "62": "Health Care and Social Assistance",
    "71": "Arts, Entertainment, and Recreation",
    "72": "Accommodation and Food Services",
    "81": "Other Services (except Public Administration)",
    "92": "Public Administration"
} as const;

const EMPLOYMENT_DENSITY_AVG = 375;

export function getJobsProxyValue(parcel: any): number | null {
    const grossSqft = Number(parcel?.site?.areaSqft?.commercialGross ?? 0);

    if (!Number.isFinite(grossSqft) || grossSqft <= 0) {
        return null;
    }

    return Math.round(grossSqft / EMPLOYMENT_DENSITY_AVG);
}

export function hasRealJobsValue(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === "string" && value.trim() === "") return false;

    const numericValue = Number(value);
    return Number.isFinite(numericValue);
}

